import React, { useState, useEffect } from 'react';
import { Container, Div, Text } from '../../styles/Common';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { theme } from '../../styles/theme';
import back from "../../assets/icons/arrow-left.svg";
import searchIcon from "../../assets/icons/search.svg";
import closeIcon from "../../assets/icons/close.svg";
import { Link } from '../../components/Link';
import { Button } from '../../components/Button';
import { EditUserModal } from '../UserManagement/components/EditUserModal';
import { Bullet, Card } from './styles';
import Switch from '../../components/Switch';
import { Input } from '../../components/Input';
import { Divider } from 'antd';
import useGetUserDetail from './hooks/useGetUserDetail';
import useDebounce from '../../core/hooks/useDebounce';
import useChangeStatus from '../UserManagement/hooks/useChangeStatus';
import useRevokeAccess from './hooks/useRevokeAccess';
import useGetContractorCompanies from '../UserManagement/hooks/useGetContractorCompanies';
import useOptions from '../../core/hooks/useOptions';

export const UserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const changeStatus = useChangeStatus();
  const revokeAccess = useRevokeAccess();
  const getUserDetail = useGetUserDetail(id);
  const { data, isSuccess, refetch } = getUserDetail;
  const [userInfo, setUserInfo] = useState();
  const { data: dataContractorCompanies, isSuccess: isSuccessContractorCompanies, refetch: refetchContractorCompanies } = useGetContractorCompanies();
  const [contractorCompaniesOptions, setContractorCompaniesOptions] = useState([]);
  const [projectAccessList, setProjectAccessList] = useState([]);
  const [searchBy, setSearchBy] = useState("");
  const searchValue = useDebounce(searchBy, 600);
  const [userInfoToUpdate, setUserInfoToUpdate] = useState({
    id: "",
    name: "",
    lastName: "",
    email: "",
    phoneCodeId: 13,
    phoneNumber: "",
    companiesContractorIds: []
  });
  const options = useOptions(5);
  const { data: optionsList } = options;
  const [codeOptions, setCodeOptions] = useState([]);

  useEffect(() => {
    if (optionsList?.groupSettings?.length > 0) {
      setCodeOptions(
        optionsList.groupSettings.map((ele) => ({
          value: ele.id,
          label: ele.code,
        }))
      );
    }
  }, [optionsList]);

  useEffect(() => {
    if (isSuccessContractorCompanies) {
      setContractorCompaniesOptions(dataContractorCompanies.data.map(company => ({
        value: company.id,
        label: company.name,
      })));
    }
  }, [isSuccessContractorCompanies, dataContractorCompanies]);

  useEffect(() => {
    if (isSuccess) {
      const userDetails = data?.data?.userDetails;
      setUserInfo({
        id: userDetails?.id,
        fullName: userDetails?.fullName,
        email: userDetails?.email,
        isActive: userDetails?.isActive ? true : false,
      });
      setUserInfoToUpdate({
        id: userDetails?.id,
        name: userDetails?.name,
        lastName: userDetails?.lastName,
        email: userDetails?.email,
        phoneCodeId: userDetails?.phoneCodeId || 13,
        phoneNumber: userDetails?.phoneNumber,
        companiesContractorIds: userDetails?.companyContractorsId,
      });
      setProjectAccessList(userDetails?.projectRoles);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (searchValue === "") {
      setProjectAccessList(data?.data?.userDetails?.projectRoles);
    } else {
      const filteredProjects = data?.data?.userDetails?.projectRoles.filter((project) =>
        project.projectName.toLowerCase().includes(searchValue.toLowerCase())
      );
      setProjectAccessList(filteredProjects);
    }
  }, [searchValue, data]);

  const [openEdit, setOpenEdit] = useState(false);

  const handleBack = () => {
    const currentParams = new URLSearchParams(searchParams);
    const queryString = currentParams.toString();
    const url = queryString ? `/user-management?${queryString}` : "/user-management";
    navigate(url);
  };

  const handleEditUser = () => {
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleChangeStatus = (event) => {
    const statusId = event ? 7 : 8;

    changeStatus.reset();
    changeStatus.mutate({ statusId, usersIds: [id] }, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  const renderStatus = () => {
    return (
      <Div align="center" gap="14.5px">
        <Text size={theme.fonts.size.default} color={theme.colors.grey}>
          {userInfo?.isActive ? "Active" : "Inactive"}
        </Text>
        <Switch checked={userInfo?.isActive} onChange={handleChangeStatus} />
      </Div>
    )
  }

  const profileInfo = [
    {
      label: "Full Name",
      value: `${userInfo?.fullName}`,
    },
    {
      label: "Email Address",
      value: userInfo?.email,
    },
    {
      label: "Status",
      value: renderStatus(),
    },
  ];

  const handleSearch = (event) => {
    setSearchBy(event.target.value);
  };

  const handleRevokeAccess = (item) => {
    const payload = {
      projectId: item.projectId,
      roleId: item.roleId,
      userId: id,
    };
    revokeAccess.reset();
    revokeAccess.mutate(payload, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  return (
    <Container>
      <EditUserModal
        openCreate={openEdit}
        handleClose={handleCloseEdit}
        userInfo={userInfoToUpdate}
        setUserInfo={setUserInfoToUpdate}
        refetch={refetch}
        contractorCompaniesOptions={contractorCompaniesOptions}
        codeOptions={codeOptions}
      />
      <Div m="0 0 15px 0">
        <Link
          onClick={handleBack}
          color={theme.colors.grey1}
          icon={<img alt="back" src={back} style={{ marginRight: "12px" }} />}
        >
          Back
        </Link>
      </Div>
      <Div width="100%" justify="space-between" align="center" m="0 0 40px 0">
        <Text
          size={theme.fonts.size.h4}
          color={theme.colors.dark}
          weight={theme.fonts.weight.bold}
        >
          User Details
        </Text>
        <Button background={theme.colors.salmon} onClick={handleEditUser}>
          Edit User
        </Button>
      </Div>
      <Div width="100%" justify="space-between">
        <Card width="60%" height="438px" padding="22px 28px 26px 33px">
          <Div justify="space-between" align="center" m="0 0 24px 0">
            <Text
              size={theme.fonts.size.h6}
              color={theme.colors.dark}
              weight={theme.fonts.weight.bold}
            >
              Project Access
            </Text>
            <Input
              placeholder="Search..."
              suffix={<img alt="search" src={searchIcon} />}
              width="263px"
              value={searchBy}
              onChange={handleSearch}
            />
          </Div>
          <Div direction="column" style={{ overflowY: "auto", maxHeight: "300px" }}>
            {projectAccessList?.map((item, index) => (
              <Div direction="column" key={index}>
                <Div justify="space-between" align="center">
                  <Div gap="8px" align="center" width="50%">
                    <Bullet />
                    <Text 
                      size={theme.fonts.size.h6} 
                      color={theme.colors.grey1} 
                      weight={theme.fonts.weight.bold}
                    >
                      {item.projectName}
                    </Text>
                  </Div>
                  <Text 
                    size={theme.fonts.size.h6} 
                    color={theme.colors.grey1} 
                    weight={theme.fonts.weight.medium}
                    style={{ width: "20%", textAlign: "left" }}
                  >
                    {item.roleName}
                  </Text>
                  <Link style={{ width: "30%", textAlign: "right" }} onClick={() => handleRevokeAccess(item)}>
                    Revoke Access
                    <img alt="close" src={closeIcon} style={{ marginLeft: "8px" }} />
                  </Link>
                </Div>
                <Divider style={{ margin: "20px 0 24px 0" }} />
              </Div>
            ))}
          </Div>
        </Card>
        <Card width="37%" height="min-content" padding="24px 25px 32px 29px">
          <Text
            size={theme.fonts.size.h6}
            color={theme.colors.dark}
            weight={theme.fonts.weight.bold}
            mb="48px"
          >
            Profile
          </Text>
          {profileInfo.map((item, index) => (
            <Div key={index} justify="space-between" align="center" m="0 0 16px 0">
              <Text
                size={theme.fonts.size.default}
                color={theme.colors.dark}
                weight={theme.fonts.weight.medium}
              >
                {item.label}
              </Text>
              <Text
                size={theme.fonts.size.default}
                color={theme.colors.grey}
              >
                {item.value}
              </Text>
            </Div>
          ))}
        </Card>
      </Div>
    </Container>
  )
};
